import { isFulfilled, isPending, isRejected, Middleware } from "@reduxjs/toolkit"

import { trackEvent } from "./awsRumClient"

type RumJourney = {
  event: `${string}.${string}`
  /** Also send a `start` event when the thunk is dispatched. */
  hasStart?: boolean
  /** Extra flat fields for the event; never pass user-entered values. */
  data?: Record<string, string | number | boolean>
}

/**
 * Thunk typePrefix -> RUM journey event. Each mapped thunk sends its event with
 * `status` (start | success | fail) and `durationMs`; case/patient/org context
 * comes from the RUM session attributes.
 */
export const RUM_JOURNEYS: Record<string, RumJourney> = {
  "PhotosService/uploadPhotographs": { event: "photo.upload", hasStart: true },
  "PhotosService/uploadPhotographArray": { event: "photo.upload", hasStart: true },
  "XrayService/uploadXray": { event: "xray.upload", hasStart: true },
  "XrayService/uploadDentalChart": { event: "dentalChart.upload", hasStart: true },
  "formService/updateFormSavedData": { event: "rx.save" },
  "clinicalService/closeCaseAndClearAllStatus": { event: "case.close" },
  "orderService/placeAlignerOrders": { event: "order.create" },
  "userService/updateUserPreferences": { event: "settings.save", data: { section: "preferences" } },
  "userService/updateUserProfile": { event: "settings.save", data: { section: "profile" } },
  "userService/updatePassword": { event: "settings.save", data: { section: "password" } },
  "userService/updateClinicalSetttings": { event: "settings.save", data: { section: "clinical" } },
  "userService/updateWorkflowSettings": { event: "settings.save", data: { section: "workflow" } },
  "userService/createUpdateOrgShippingAddress": { event: "settings.save", data: { section: "shippingAddress" } },
  "userService/saveCreditCard": { event: "settings.save", data: { section: "creditCard" } },
  "userService/updateCreditCard": { event: "settings.save", data: { section: "creditCard" } },
}

const startedAtByRequestId = new Map<string, number>()

const getTypePrefix = (type: string) => type.slice(0, type.lastIndexOf("/"))

// Uploads can settle after the user navigates away, so keep the thunk's own IDs.
const getArgIds = (arg: unknown): Record<string, string> => {
  const { patientId, caseId } = (arg ?? {}) as { patientId?: unknown; caseId?: unknown }
  return {
    ...(patientId != null && patientId !== "" && { patientId: String(patientId) }),
    ...(caseId != null && caseId !== "" && { caseId: String(caseId) }),
  }
}

export const rumJourneyMiddleware: Middleware = () => (next) => (action) => {
  if (isPending(action) || isFulfilled(action) || isRejected(action)) {
    const journey = RUM_JOURNEYS[getTypePrefix(action.type)]
    const { requestId, arg } = action.meta
    if (journey && isPending(action)) {
      startedAtByRequestId.set(requestId, performance.now())
      if (journey.hasStart) {
        trackEvent(journey.event, { ...journey.data, ...getArgIds(arg), status: "start" })
      }
    } else if (journey) {
      const startedAt = startedAtByRequestId.get(requestId)
      startedAtByRequestId.delete(requestId)
      trackEvent(journey.event, {
        ...journey.data,
        ...getArgIds(arg),
        status: isFulfilled(action) ? "success" : "fail",
        ...(startedAt !== undefined && {
          durationMs: Math.round(performance.now() - startedAt),
        }),
      })
    }
  }
  return next(action)
}
